import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, AudioLines, Camera, File as FileIcon, FileText, Image as ImageIcon, Loader2, Mic, Paperclip, StickyNote, Type, X } from 'lucide-react';
import { Sheet } from '@/components/Sheet';
import { dictate, DICTATE_EMPTY, DICTATE_FAILED } from '@/api/chatApi';
import { useVoiceCapture } from '@/hooks/useVoiceCapture';
import { fileKind, pickFiles, type Rejected } from '@/lib/attachments';
import { composerRules, planFileMessages, rejectReason, type ComposerRules } from '@/lib/channelRules';
import { formatBytes } from '@/lib/messageModel';
import { idleGesture, move, press, release, type Gesture } from '@/lib/voice/micGesture';
import { prepareVoiceFile } from '@/lib/voice/prepareVoice';
import { clock } from '@/lib/voice/take';

interface Props {
  /** Клиенту ответить можно (окно канала открыто). Заметки можно всегда. */
  canReply: boolean;
  onSend: (text: string, isNote: boolean) => void;
  /** Файлы с подписью (или голосовое с isVoice) — одним сообщением. */
  onSendFiles: (input: { files: File[]; caption: string; isPrivate: boolean; isVoice?: boolean }) => void;
  /** Короткое сообщение внизу экрана. */
  onToast: (text: string) => void;
  /** Правила канала (голос, виды файлов, по одному ли, подпись). Нет — как раньше. */
  rules?: ComposerRules;
}

/** Заметка — для своих, клиенту не уходит: правила канала к ней не относятся. */
const NOTE_RULES = composerRules(null);

/** Enter отправляет только там, где есть настоящая клавиатура и мышь; на телефоне Enter — новая строка. */
function isDesktopInput(): boolean {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return false;
  }
}

interface Picked { file: File; url: string | null }

/** «Фото или видео» / «Фото» / «Видео» — по тому, что канал принимает. */
function mediaLabel(r: ComposerRules): string {
  if (r.kinds.image && r.kinds.video) return 'Фото или видео';
  return r.kinds.image ? 'Фото' : 'Видео';
}

/** Превью выбранного файла: для картинок — сама картинка из памяти, для остальных — значок и имя. */
function PickedChip({ p, onRemove }: { p: Picked; onRemove: () => void }) {
  const kind = fileKind(p.file);
  return (
    <div className="pk">
      {kind === 'image' && p.url ? <img src={p.url} alt="" /> : (
        <span className="pk-f">{kind === 'file' ? <FileText aria-hidden /> : kind === 'video' ? <ImageIcon aria-hidden /> : <AudioLines aria-hidden />}</span>
      )}
      <span className="pk-n">{p.file.name}<small>{formatBytes(p.file.size)}</small></span>
      <button type="button" className="pk-x" aria-label={`Убрать ${p.file.name}`} onClick={onRemove}><X aria-hidden /></button>
    </div>
  );
}

/**
 * Поле ввода 1:1 как в ГиперЧАТе (packages/chat-ui/src/components/Composer.tsx), без кружка:
 * скрепка · растущее поле (до 110 px) · микрофон (пропадает при наборе) · кнопка отправки с градиентом.
 * Жёлтая монета на стыке включает режим заметки: поле целиком жёлтое, кнопка становится крестиком.
 * Заметка работает и для голоса, и для файлов (уходят с private).
 */
export function Composer({ canReply, onSend, onSendFiles, onToast, rules: channelRules }: Props) {
  const [text, setText] = useState('');
  const [note, setNote] = useState(false);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const photoIn = useRef<HTMLInputElement>(null);
  const cameraIn = useRef<HTMLInputElement>(null);
  const fileIn = useRef<HTMLInputElement>(null);
  const capture = useVoiceCapture();
  const has = text.trim().length > 0;
  const hasFiles = picked.length > 0;
  const blocked = !canReply && !note;
  const base = channelRules ?? NOTE_RULES;
  const rules: ComposerRules = note ? { ...NOTE_RULES, serverNormalizes: base.serverNormalizes } : base;

  // Жест микрофона: нажатие слушаем на window с самого начала — палец мог уйти с кнопки.
  const gesture = useRef<Gesture>(idleGesture);
  const releasedEarly = useRef(false);
  const [locked, setLocked] = useState(false);

  // Поле растёт под текст, но не выше 110 px: иначе длинный текст съедает ленту.
  useEffect(() => {
    const n = area.current;
    if (!n) return;
    n.style.height = 'auto';
    n.style.height = `${Math.min(n.scrollHeight, 110)}px`;
  }, [text]);

  // Ссылки на превью освобождаем при уходе с экрана.
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  useEffect(() => () => pickedRef.current.forEach((p) => p.url && URL.revokeObjectURL(p.url)), []);

  const submit = () => {
    if (blocked) return;
    if (hasFiles) {
      // Канал берёт по одному файлу или без подписи — раскладываем по сообщениям, как панель Chatwoot.
      for (const part of planFileMessages(picked.map((p) => p.file), text, rules)) {
        if (part.files.length) onSendFiles({ files: part.files, caption: part.caption, isPrivate: note });
        else if (part.caption) onSend(part.caption, note);
      }
      picked.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      setPicked([]);
      setText('');
      return;
    }
    const v = text.trim();
    if (!v) return;
    onSend(v, note);
    setText('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey && isDesktopInput()) {
      e.preventDefault();
      submit();
    }
  };

  // ── Файлы ──
  const addFiles = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    // Сначала правила канала (вид файла, предел канала), затем общий предел и число файлов.
    const unfit: Rejected[] = [];
    const fit = files.filter((f) => {
      const why = rejectReason(f, rules);
      if (why) unfit.push({ name: f.name, reason: why });
      return !why;
    });
    const picked2 = pickFiles(fit, picked.length);
    const accepted = picked2.accepted;
    const rejected = [...unfit, ...picked2.rejected];
    if (rejected.length) {
      onToast(rejected.length === 1 ? `«${rejected[0].name}»: ${rejected[0].reason}` : `Не добавлено файлов: ${rejected.length}. ${rejected[0].reason}`);
    }
    setPicked((cur) => [
      ...cur,
      ...accepted.map((file) => ({ file, url: fileKind(file) === 'image' ? URL.createObjectURL(file) : null })),
    ]);
    setAttachOpen(false);
    area.current?.focus();
  };
  const removePicked = (i: number) => {
    setPicked((cur) => {
      const gone = cur[i];
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return cur.filter((_, k) => k !== i);
    });
  };

  // ── Голос ──
  // Обработчики жеста создаём заново на каждое нажатие и снимаем теми же самыми функциями.
  const captureRef = useRef(capture);
  captureRef.current = capture;
  const listeners = useRef<{ up: () => void; mv: (e: PointerEvent) => void } | null>(null);
  const cleanup = useCallback(() => {
    const l = listeners.current;
    if (!l) return;
    window.removeEventListener('pointerup', l.up);
    window.removeEventListener('pointercancel', l.up);
    window.removeEventListener('pointermove', l.mv);
    listeners.current = null;
  }, []);
  useEffect(() => cleanup, [cleanup]);

  const micDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    cleanup();
    gesture.current = press(Date.now(), e.clientY);
    releasedEarly.current = false;
    setLocked(false);
    const up = () => {
      cleanup();
      const r = release(gesture.current, Date.now());
      gesture.current = r.gesture;
      if (r.action === 'lock') setLocked(true);
      if (r.action === 'stop') {
        // Палец отпущен раньше, чем микрофон успел включиться (первый запрос разрешения): остановим при старте.
        if (captureRef.current.isRecording()) captureRef.current.stop();
        else releasedEarly.current = true;
      }
    };
    const mv = (ev: PointerEvent) => {
      const g = move(gesture.current, ev.clientY);
      if (g !== gesture.current) {
        gesture.current = g;
        if (g.phase === 'locked') setLocked(true);
      }
    };
    listeners.current = { up, mv };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('pointermove', mv);
    void capture.start().then((ok) => {
      if (!ok) { cleanup(); gesture.current = idleGesture; return; }
      if (releasedEarly.current) { releasedEarly.current = false; captureRef.current.stop(); }
    });
  };
  // Клавиатурный путь (скринридер, внешняя клавиатура): Enter/пробел на микрофоне = «нажал один раз».
  const micKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    setLocked(true);
    void capture.start();
  };

  const sendTakeAsVoice = async () => {
    const take = capture.take;
    if (!take || preparing) return;
    setPreparing(true);
    try {
      const { file } = await prepareVoiceFile(take.blob, take.mime, { serverNormalizes: rules.serverNormalizes });
      onSendFiles({ files: [file], caption: '', isPrivate: note, isVoice: true });
      capture.clear();
    } finally {
      setPreparing(false);
    }
  };

  const takeToText = async () => {
    const take = capture.take;
    if (!take || recognizing) return;
    setRecognizing(true);
    try {
      const spoken = (await dictate(take.blob)).trim();
      if (!spoken) {
        capture.setError(DICTATE_EMPTY);
        return;
      }
      const next = text.trim() ? `${text.trimEnd()} ${spoken}` : spoken;
      setText(next);
      capture.clear();
      requestAnimationFrame(() => {
        const n = area.current;
        if (!n) return;
        n.focus();
        n.setSelectionRange(next.length, next.length);
      });
    } catch (e) {
      capture.setError((e as Error).message || DICTATE_FAILED);
    } finally {
      setRecognizing(false);
    }
  };

  const placeholder = note
    ? 'Заметка для своих, клиент не увидит'
    : canReply ? 'Написать сообщение' : 'Клиенту сейчас нельзя ответить. Можно написать заметку';

  const recording = capture.phase === 'recording';
  const recorded = capture.phase === 'recorded' && capture.take;

  return (
    <div className={`composer${note && !recording ? ' is-note' : ''}`}>
      {!recording && !recorded && (
        <>
          <button
            type="button"
            className="notebtn"
            aria-pressed={note}
            aria-label={note ? 'Выключить заметку' : 'Написать заметку для своих'}
            onClick={() => { setNote((v) => !v); area.current?.focus(); }}
          >
            <span className="n"><StickyNote aria-hidden /></span>
            <span className="x"><X aria-hidden /></span>
          </button>
          <span className="notetag">Заметка</span>
        </>
      )}

      {hasFiles && !recording && !recorded && (
        <div className="picked" role="list" aria-label="Выбранные файлы">
          {picked.map((p, i) => (
            <div role="listitem" key={`${p.file.name}-${i}`}><PickedChip p={p} onRemove={() => removePicked(i)} /></div>
          ))}
        </div>
      )}

      {recording ? (
        <div className="rec" role="group" aria-label="Идёт запись">
          {/* Отмена — на нажатии, а не на щелчке: иначе раньше неё сработает «палец отпущен — запись готова». */}
          <button
            type="button"
            className="rx"
            aria-label="Отменить запись"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cleanup();
              gesture.current = idleGesture;
              capture.cancel();
            }}
          ><X aria-hidden /></button>
          <span className="rwave" aria-hidden>
            {capture.levels.map((v, i) => <i key={i} style={{ height: `${Math.max(4, v * 32)}px` }} />)}
          </span>
          {!locked && <span className="rhint">вверх — закрепить</span>}
          <span className="rtime tnum" data-long={capture.seconds >= 300 ? 'true' : undefined}>{clock(capture.seconds)}</span>
          <button
            type="button"
            className="rstop"
            title="Остановить"
            aria-label="Остановить запись"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => capture.stop()}
          ><ArrowUp aria-hidden strokeWidth={3} /></button>
        </div>
      ) : recorded ? (
        <div className="takewrap">
          <div className="choice" role="group" aria-label="Что сделать с записью">
            <button type="button" onClick={() => void takeToText()} disabled={recognizing || preparing}>
              {recognizing ? <Loader2 className="spin-i" aria-hidden /> : <Type aria-hidden />}
              {recognizing ? 'Распознаю…' : 'Текстом'}
            </button>
            {rules.voiceAsAudio && (
              <button type="button" className="pri" onClick={() => void sendTakeAsVoice()} disabled={recognizing || preparing}>
                {preparing ? <Loader2 className="spin-i" aria-hidden /> : <AudioLines aria-hidden />}
                {preparing ? 'Готовлю…' : 'Голосом'}
              </button>
            )}
          </div>
          {!rules.voiceAsAudio && rules.voiceHint && <div className="chint" role="note">{rules.voiceHint}</div>}
          <div className="take">
            <button type="button" className="rx" onClick={capture.cancel} disabled={recognizing || preparing} aria-label="Удалить запись">
              <X aria-hidden />
            </button>
            <span className="twave" aria-label={`Запись ${clock(Math.round(recorded.durationMs / 1000))}`}>
              {recorded.peaks.map((v, i) => <i key={i} style={{ height: `${Math.max(12, Math.round(v * 100))}%` }} />)}
            </span>
            <span className="tdur tnum">{clock(Math.round(recorded.durationMs / 1000))}</span>
          </div>
        </div>
      ) : (
        <div className="cbar">
          {rules.canAttach && (
            <button type="button" className="cib" aria-label="Прикрепить файл" onClick={() => setAttachOpen(true)}>
              <Paperclip aria-hidden />
            </button>
          )}
          <textarea
            ref={area}
            className="cin"
            rows={1}
            aria-label="Текст сообщения"
            placeholder={hasFiles ? 'Подпись к файлам' : placeholder}
            value={text}
            enterKeyHint="enter"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {!has && !hasFiles && (
            <button
              type="button"
              className="cib"
              aria-label="Надиктовать"
              title="Нажмите — запись пойдёт; удерживайте — запись до отпускания"
              disabled={blocked}
              onPointerDown={micDown}
              onKeyDown={micKey}
            >
              <Mic aria-hidden />
            </button>
          )}
          <button
            type="button"
            className={`csend${(has || hasFiles) && !blocked ? ' on' : ''}`}
            aria-label={note ? 'Сохранить заметку' : 'Отправить'}
            disabled={(!has && !hasFiles) || blocked}
            onClick={submit}
          >
            <ArrowUp aria-hidden strokeWidth={3} />
          </button>
        </div>
      )}

      {capture.error && !recording && (
        <div className="chint warnhint" role="alert">
          {capture.error}
          <button type="button" className="retry" onClick={() => capture.setError(null)}>понятно</button>
        </div>
      )}

      {/* Скрытые поля выбора. Нажатие на пункт листа вызывает .click() прямо в обработчике — иначе WebView
          не откроет выбор файла (нужен жест человека). */}
      <input ref={photoIn} type="file" accept={rules.mediaAccept || undefined} multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={cameraIn} type="file" accept={rules.mediaAccept || undefined} capture="environment" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={fileIn} type="file" accept={rules.fileAccept || undefined} multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

      {attachOpen && (
        <Sheet label="Что прикрепить" onClose={() => setAttachOpen(false)}>
          <div className="sh"><h3>Прикрепить</h3></div>
          <div className="sb" style={{ gap: 2 }}>
            {rules.sheet.media && (
              <button type="button" className="opt" onClick={() => photoIn.current?.click()}>
                <ImageIcon aria-hidden /><span className="t">{mediaLabel(rules)}<small>Из галереи телефона</small></span>
              </button>
            )}
            {rules.sheet.camera && (
              <button type="button" className="opt" onClick={() => cameraIn.current?.click()}>
                <Camera aria-hidden /><span className="t">Камера<small>Снять и сразу отправить</small></span>
              </button>
            )}
            {rules.sheet.file && (
              <button type="button" className="opt" onClick={() => fileIn.current?.click()}>
                <FileIcon aria-hidden /><span className="t">{rules.kinds.file ? 'Файл' : 'Аудиофайл'}
                  <small>{rules.kinds.file ? 'Документ, таблица, архив' : 'Этот канал документы не принимает'}</small></span>
              </button>
            )}
            {rules.splitFiles && <p className="chint">В этот канал каждый файл уходит отдельным сообщением</p>}
          </div>
        </Sheet>
      )}
    </div>
  );
}
